import { useState, useMemo } from "react";
import { TopBarRoute } from "components/templates";
import { Stack, Field, Title, Select } from "@introist/react-foundation/v2";
import { Skeletons } from "components/molecules";
import { Empty } from "foundation";
import { MetricsCard } from "../components/MetricsCard";
import { CategoryMetricsTable } from "../components/CategoryMetricsTable";
import { ROICalculator } from "../components/ROICalculator";
import { useAnalyticsData } from "../hooks/useAnalyticsData";
import { ExportData } from "modules/employees/containers/ExportData";
import styled from "styled-components";

const SectionTitle = styled(Title)`
  margin-top: 32px;
  margin-bottom: 16px;
`;

type TimePeriod =
  | "last-7-days"
  | "last-30-days"
  | "last-90-days"
  | "last-6-months"
  | "last-12-months"
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "this-year"
  | "last-year";

const TIME_PERIOD_OPTIONS = [
  { key: "last-7-days", title: "Last 7 Days" },
  { key: "last-30-days", title: "Last 30 Days" },
  { key: "last-90-days", title: "Last 90 Days" },
  { key: "last-6-months", title: "Last 6 Months" },
  { key: "last-12-months", title: "Last 12 Months" },
  { key: "this-month", title: "This Month" },
  { key: "last-month", title: "Last Month" },
  { key: "this-quarter", title: "This Quarter" },
  { key: "last-quarter", title: "Last Quarter" },
  { key: "this-year", title: "This Year" },
  { key: "last-year", title: "Last Year" }
];

const getDateRangeForPeriod = (
  period: TimePeriod
): { startDate?: string; endDate?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let startDate: Date;
  const endDate = today;

  switch (period) {
    case "last-7-days":
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 7);
      break;
    case "last-30-days":
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 30);
      break;
    case "last-90-days":
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 90);
      break;
    case "last-6-months":
      startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - 6);
      break;
    case "last-12-months":
      startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - 12);
      break;
    case "this-month":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "last-month":
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate.setDate(0); // Last day of previous month
      break;
    case "this-quarter":
      const currentQuarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
      break;
    case "last-quarter":
      const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
      const lastQuarterYear = lastQuarter < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastQuarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
      startDate = new Date(lastQuarterYear, lastQuarterMonth, 1);
      endDate.setMonth(lastQuarterMonth + 3, 0); // Last day of last quarter
      break;
    case "this-year":
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    case "last-year":
      startDate = new Date(now.getFullYear() - 1, 0, 1);
      endDate.setFullYear(now.getFullYear() - 1, 11, 31);
      break;
    default:
      return {};
  }

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0]
  };
};

export const AnalyticsDashboardRoute = () => {
  const [period, setPeriod] = useState<TimePeriod>("last-90-days");

  const dateRange = useMemo(() => getDateRangeForPeriod(period), [period]);

  const { overview, categories, isLoading, error } = useAnalyticsData(dateRange);

  return (
    <TopBarRoute title="Analytics Dashboard" medium>
      <Stack vertical gap="xLarge">
        {/* Time Period Filter */}
        <div style={{ maxWidth: "300px" }}>
          <Field title="Time Period">
            <Select
              options={TIME_PERIOD_OPTIONS}
              value={period}
              onSelect={option => setPeriod(option.key as TimePeriod)}
            />
          </Field>
        </div>

        {/* Loading State */}
        {isLoading && <Skeletons.List />}

        {/* Error State */}
        {error && (
          <Empty
            title="Failed to load analytics"
            icon="crossCircle"
            message="Please try again or contact support"
          />
        )}

        {/* Overview Metrics */}
        {!isLoading && !error && overview.data && (
          <>
            <div>
              <Title>Overview</Title>
              <Stack gap="medium" style={{ marginTop: "16px", flexWrap: "wrap" }}>
                <MetricsCard
                  title="Total Employees"
                  value={overview.data.totalEmployees.toLocaleString()}
                  tooltip="Unique employees who have been activated at least once"
                />
                <MetricsCard
                  title="Total Automations"
                  value={overview.data.totalAutomations.toLocaleString()}
                  tooltip="Total number of workflow automations launched"
                />
                <MetricsCard
                  title="Total Steps Completed"
                  value={overview.data.totalStepsCompleted.toLocaleString()}
                  tooltip="Total number of workflow steps that have been executed and completed"
                />
              </Stack>
            </div>

            {/* Category Metrics */}
            <div>
              <Stack justifyContent="space-between" alignItems="center">
                <SectionTitle style={{ margin: 0 }}>Performance by Category</SectionTitle>
                {categories.data && categories.data.length > 0 && (
                  <ExportData
                    data={categories.data}
                    title="Export to Excel"
                    tooltip="Export category performance data to Excel"
                    fileName="category-performance.xls"
                  />
                )}
              </Stack>
              <div style={{ marginTop: "16px" }}>
                {categories.data && categories.data.length > 0 ? (
                  <CategoryMetricsTable categories={categories.data} />
                ) : (
                  <Empty
                    title="No category data"
                    icon="folder"
                    message="No workflows have been categorized yet. Add metadata.category to workflows to see category metrics."
                  />
                )}
              </div>
            </div>

            {/* ROI Calculator */}
            <div>
              <SectionTitle>Return on Investment</SectionTitle>
              <ROICalculator
                totalWorkflows={overview.data.totalAutomations}
                totalSteps={overview.data.totalStepsCompleted}
              />
            </div>
          </>
        )}
      </Stack>
    </TopBarRoute>
  );
};
